import { useQuery } from "@tanstack/react-query";
import type { DashboardAreaCurrentPopulationResponse } from "@situate/contracts/runtime";
import { useRuntimeHttpClient } from "@/features/runtime-session/hooks/use-runtime-http-client";

const REFETCH_INTERVAL_MS = 10_000;
const STALE_TIME_MS = 8_000;

function buildAreaCurrentPopulationPath(areaId: string): string {
  return `/v1/dashboard/areas/${encodeURIComponent(areaId)}/current-population`;
}

export interface AreaCurrentPopulationState {
  population: number | null;
  dataStatus: "current" | "stale" | "unavailable";
  calculatedAt: string | null;
  isLoading: boolean;
  error: Error | null;
}

/**
 * Fetches the current total population for a deployment area from the runtime API.
 *
 * Re-fetches every 10 seconds to match the 10s source data granularity.
 *
 * Disabled until a runtime session client exists for the deployment and the
 * `dashboard_area_current_population` operation has been granted.
 */
export function useAreaCurrentPopulation(
  deploymentId: string,
  areaId: string | undefined,
  enabled = true
): AreaCurrentPopulationState {
  const runtime = useRuntimeHttpClient();
  const hasGrant = runtime.operationGrants.some(
    (grant) => grant.operation_id === "dashboard_area_current_population"
  );
  const client = runtime.client;

  const query = useQuery({
    queryKey: ["area-current-population", deploymentId, areaId] as const,
    queryFn: async ({ signal }) => {
      const response = await client!.get<DashboardAreaCurrentPopulationResponse>(
        buildAreaCurrentPopulationPath(areaId as string),
        { signal }
      );
      return response;
    },
    enabled:
      enabled &&
      Boolean(client) &&
      runtime.deploymentId === deploymentId &&
      hasGrant &&
      areaId != null,
    staleTime: STALE_TIME_MS,
    refetchInterval: REFETCH_INTERVAL_MS,
  });

  return {
    population: query.data?.population ?? null,
    dataStatus: query.data?.data_status ?? "unavailable",
    calculatedAt: query.data?.calculated_at ?? null,
    isLoading: query.isLoading,
    error: query.error,
  };
}
